import { initEditor, updateEditorContent, getEditorMarkdown, getEditorHTML, hasEditor, getEditor } from './editor.js';
import { updateAIChatContext } from './ai.js';
import { updateSectionNav, clearSectionNav, updateSectionNavCompletion } from './section-nav.js';
import { updateVersionBadge } from './version-badge.js';
import { parseCrdMeta, updateCrdMeta } from './crd-meta.js';
import { initSectionComplete, clearSectionComplete, updateMeta } from './section-complete.js';
import { getCurrentUser, isRepoOwner } from './auth.js';
import { loadComments, clearComments, restoreCommentHighlights } from './comments.js';
import { apiPost, apiPut, apiDelete } from './api.js';

let currentFolder = 'requests';
let currentFile = null;
let currentFileSha = null;
let currentMeta = null;
let previousSectionStates = null;
let autoSaveInterval = null;
let autoSaveTimeout = null; // Debounce timer for auto-save
let hasUnsavedChanges = false;
let lastSavedContent = '';
let githubConfig = null; // GitHub config (owner, repo, docsPath)

// Load GitHub config
async function loadGitHubConfig() {
  if (!githubConfig) {
    try {
      const res = await fetch('/api/config');
      githubConfig = await res.json();
      console.log('⚙️ GitHub config loaded:', githubConfig);
    } catch (error) {
      console.error('Failed to load GitHub config:', error);
      // Fallback to default
      githubConfig = { owner: '', repo: '', docsPath: 'docs/crds', branch: 'develop' };
    }
  }
  return githubConfig;
}

// Toast notification function
export function showToast(message, type = 'info') {
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  
  // Trigger animation
  setTimeout(() => toast.classList.add('show'), 10);
  
  // Remove after 3 seconds
  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

export function initFileBrowser() {
  // Create accordion structure
  const folders = ['requests', 'backlog', 'active', 'archived'];
  const fileListDiv = document.getElementById('files');
  
  // Build accordion HTML
  const accordionHTML = folders.map(folder => `
    <div class="folder-section" data-folder="${folder}">
      <div class="folder-header">
        <i class="ph ph-caret-right chevron"></i>
        <span class="folder-name">${folder.charAt(0).toUpperCase() + folder.slice(1)}</span>
      </div>
      <div class="folder-files"></div>
    </div>
  `).join('');
  
  fileListDiv.innerHTML = accordionHTML;
  
  // Auto-expand requests folder and load files on init
  const requestsSection = document.querySelector('[data-folder="requests"]');
  if (requestsSection) {
    const filesDiv = requestsSection.querySelector('.folder-files');
    const chevron = requestsSection.querySelector('.chevron');
    if (filesDiv && chevron) {
      filesDiv.style.display = 'block';
      chevron.classList.remove('ph-caret-right');
      chevron.classList.add('ph-caret-down');
      loadFilesIntoSection('requests', filesDiv);
    }
  }
  
  // Add click handlers to folder headers
  document.querySelectorAll('.folder-header').forEach(header => {
    header.addEventListener('click', () => {
      const section = header.parentElement;
      if (!section) return;
      const folder = section.dataset?.folder;
      if (!folder) return;
      const filesDiv = section.querySelector('.folder-files');
      const chevron = header.querySelector('.chevron');
      
      // Toggle expanded state
      const isExpanded = filesDiv.style.display === 'block';
      
      if (isExpanded) {
        filesDiv.style.display = 'none';
        chevron.classList.remove('ph-caret-down');
        chevron.classList.add('ph-caret-right');
      } else {
        filesDiv.style.display = 'block';
        chevron.classList.remove('ph-caret-right');
        chevron.classList.add('ph-caret-down');
        
        // Load files if not already loaded
        if (filesDiv.children.length === 0) {
          loadFilesIntoSection(folder, filesDiv);
        }
      }
    });
    
    // Add drop zone handlers for repo owners
    console.log('[initFileBrowser] isRepoOwner check:', isRepoOwner());
    if (isRepoOwner()) {
      const section = header.parentElement;
      const folder = section?.dataset?.folder;
      if (folder) {
        console.log('[initFileBrowser] Adding drop zone to folder:', folder);
        header.classList.add('drop-zone');
        header.dataset.folder = folder;
        header.addEventListener('dragover', handleDragOver);
        header.addEventListener('dragleave', handleDragLeave);
        header.addEventListener('drop', handleDrop);
      }
    }
  });

  // Explorer toggle button
  const explorerBtn = document.getElementById('openExplorerBtn');
  const sidebar = document.getElementById('fileBrowser');

  function toggleExplorer() {
    const isOpen = !sidebar.classList.contains('collapsed');
    
    if (isOpen) {
      // Close explorer
      sidebar.classList.add('collapsed');
      explorerBtn.classList.remove('active');
      explorerBtn.querySelector('span').textContent = 'Open explorer';
    } else {
      // Open explorer
      sidebar.classList.remove('collapsed');
      explorerBtn.classList.add('active');
      explorerBtn.querySelector('span').textContent = 'Close explorer';
    }
  }

  // Set initial state
  explorerBtn.classList.add('active');
  explorerBtn.querySelector('span').textContent = 'Close explorer';

  // Wire up both close buttons to same toggle
  document.getElementById('collapseBtn').addEventListener('click', toggleExplorer);
  explorerBtn.addEventListener('click', toggleExplorer);

  document.getElementById('saveBtn').addEventListener('click', (e) => {
    e.preventDefault();
    saveFile(); // Don't pass the event object
  });
  document.getElementById('deleteBtn').addEventListener('click', confirmDelete);
  
  // Save dropdown toggle
  const saveDropdownToggle = document.getElementById('saveDropdownToggle');
  const saveDropdownMenu = document.getElementById('saveDropdownMenu');
  const publishBtn = document.getElementById('publishBtn');
  
  // Helper function to bump patch version (e.g., "0.1.0" → "0.1.1")
  function bumpPatchVersion(versionString) {
    const parts = versionString.split('.');
    const patch = parseInt(parts[2] || 0, 10) + 1;
    return `${parts[0]}.${parts[1]}.${patch}`;
  }
  
  // Helper function to get minor version (e.g., "0.1.1" → "0.1")
  function getMinorVersion(versionString) {
    const parts = versionString.split('.');
    return `${parts[0]}.${parts[1]}`;
  }
  
  // Helper function to update H1 version
  function updateH1Version(content, newMinorVersion) {
    const lines = content.split('\n');
    const h1Index = lines.findIndex(line => line.trim().startsWith('# '));
    
    if (h1Index !== -1) {
      const h1Line = lines[h1Index];
      // Replace existing version pattern (v0.1, v0.2.3, etc.) or append if none exists
      const versionPattern = /\s+v\d+\.\d+(?:\.\d+)?/;
      if (versionPattern.test(h1Line)) {
        lines[h1Index] = h1Line.replace(versionPattern, ` v${newMinorVersion}`);
      } else {
        lines[h1Index] = h1Line.trim() + ` v${newMinorVersion}`;
      }
    }
    
    return lines.join('\n');
  }
  
  // Helper function to calculate new version based on section completion
  function calculateNewVersion(currentVersion, prevSections, currentSections) {
    console.log('🔢 calculateNewVersion called:', {
      currentVersion,
      prevSections,
      currentSections
    });
    
    const parts = currentVersion.split('.').map(Number);
    const major = parts[0];
    const minor = parts[1];
    const patch = parts[2];
    
    // Pre-dev sections (order doesn't matter, just count)
    const preDevSections = ['requirements', 'design_specs', 'a11y_approval', 'cms', 'dev_guidance'];
    
    // Count completed sections
    const prevPreDevCount = preDevSections.filter(s => prevSections && prevSections[s] === 'complete').length;
    const currentPreDevCount = preDevSections.filter(s => currentSections && currentSections[s] === 'complete').length;
    
    console.log('🔢 Section counts:', { prevPreDevCount, currentPreDevCount });
    
    // Check if all pre-dev sections are complete
    const allPreDevComplete = currentPreDevCount === 5;
    
    // Check if docs just completed (post-dev)
    const docsJustCompleted = currentSections && currentSections.docs === 'complete' && 
                              (!prevSections || prevSections.docs !== 'complete');
    
    // Pre-dev version bumping (major = 0)
    if (major === 0) {
      // If pre-dev count increased, bump to appropriate minor version
      if (currentPreDevCount > prevPreDevCount) {
        if (currentPreDevCount === 5) {
          // All pre-dev sections complete
          console.log('🔢 All pre-dev complete → 0.9.0');
          return '0.9.0';
        }
        // Minor version = count + 1 (1 complete = 0.2, 2 complete = 0.3, etc.)
        const newVersion = `0.${currentPreDevCount + 1}.0`;
        console.log('🔢 Section count increased →', newVersion);
        return newVersion;
      }
      // No new sections completed in pre-dev: patch bump
      const patchVersion = `${major}.${minor}.${patch + 1}`;
      console.log('🔢 Patch bump →', patchVersion);
      return patchVersion;
    }
    
    // Post-dev version bumping (major >= 1)
    if (major >= 1 && docsJustCompleted) {
      return `${major}.${minor + 1}.0`;
    }
    
    // Default: patch bump for any content changes
    return `${major}.${minor}.${patch + 1}`;
  }
  
  // Helper function to get next patch version (for display only)
  function getNextPatchVersion(currentVersion) {
    if (!previousSectionStates || !currentMeta) {
      return bumpPatchVersion(currentVersion);
    }
    return calculateNewVersion(currentVersion, previousSectionStates, currentMeta.sections);
  }
  
  saveDropdownToggle?.addEventListener('click', (e) => {
    e.stopPropagation();
    saveDropdownMenu.classList.toggle('open');
    
    // Update publish button text with next version when dropdown opens
    if (saveDropdownMenu.classList.contains('open') && currentMeta && publishBtn) {
      const nextVersion = getNextPatchVersion(currentMeta.version);
      publishBtn.innerHTML = `<i class="ph ph-rocket-launch"></i> Publish v${nextVersion}`;
    }
  });
  
  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.save-dropdown')) {
      saveDropdownMenu?.classList.remove('open');
    }
  });
  
  // Publish version function
  // Helper function to format date in dd-mm-yy format
  function formatUKDate() {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }

  // Helper function to insert changelog entry into markdown
  function insertChangelogEntry(markdown, date, version, author, entry) {
    console.log('🔍 Looking for changelog table...');
    console.log('📄 Markdown length:', markdown.length);
    
    // Find the Changelog section and log what's there
    const changelogIdx = markdown.indexOf('Changelog');
    if (changelogIdx > -1) {
      console.log('📋 Found "Changelog" at index:', changelogIdx);
      console.log('📋 Content around changelog (300 chars):');
      console.log(markdown.substring(changelogIdx, changelogIdx + 300));
    } else {
      console.log('❌ No "Changelog" text found in markdown');
    }
    
    // First, check if changelog table is HTML (TipTap outputs HTML tables)
    const htmlTableMatch = markdown.match(/## Changelog\s*\n+<table[^>]*>([\s\S]*?)<\/table>/i);
    
    if (htmlTableMatch) {
      console.log('📋 Found HTML changelog table');
      
      const safeEntry = entry.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      
      // Create new row as HTML
      const newRowHtml = `<tr><td colspan="1" rowspan="1"><p>${date}</p></td><td colspan="1" rowspan="1"><p>${version}</p></td><td colspan="1" rowspan="1"><p>${author}</p></td><td colspan="1" rowspan="1"><p>${safeEntry}</p></td></tr>`;
      
      // Find the first </tr> after the header row and insert after it
      const tableStart = markdown.indexOf(htmlTableMatch[0]);
      const tableContent = htmlTableMatch[0];
      
      // Find end of first row (header row)
      const firstRowEnd = tableContent.indexOf('</tr>') + 5;
      
      // Insert new row after header
      const updatedTable = tableContent.substring(0, firstRowEnd) + newRowHtml + tableContent.substring(firstRowEnd);
      
      const updated = markdown.substring(0, tableStart) + updatedTable + markdown.substring(tableStart + htmlTableMatch[0].length);
      
      console.log('✅ Changelog row inserted into HTML table');
      return updated;
    }
    
    // Try multiple patterns for the table separator row
    // Pattern 1: Standard markdown table separator with dashes (flexible spacing)
    const tableRegex1 = /(\|\s*-+\s*\|\s*-+\s*\|\s*-+\s*\|\s*-+\s*\|)/;
    // Pattern 2: Exact three-dash pattern
    const tableRegex2 = /(\|\s*---\s*\|\s*---\s*\|\s*---\s*\|\s*---\s*\|)/;
    // Pattern 3: Colons (alignment) in separators
    const tableRegex3 = /(\|\s*:?-+:?\s*\|\s*:?-+:?\s*\|\s*:?-+:?\s*\|\s*:?-+:?\s*\|)/;
    // Pattern 4: Look for table after Changelog heading
    const changelogSectionRegex = /(## Changelog\s*\n+\|[^\n]+\|\s*\n)(\|[^\n]+\|)/;
    
    console.log('📋 Testing patterns:');
    console.log('  Pattern 1 (flexible dashes):', tableRegex1.test(markdown));
    console.log('  Pattern 2 (exact ---):',  tableRegex2.test(markdown));
    console.log('  Pattern 3 (with colons):', tableRegex3.test(markdown));
    console.log('  Pattern 4 (section heading):', changelogSectionRegex.test(markdown));
    
    let match = markdown.match(tableRegex1);
    let usedPattern = 'pattern1';
    
    if (!match) {
      match = markdown.match(tableRegex2);
      usedPattern = 'pattern2';
    }
    
    if (!match) {
      match = markdown.match(tableRegex3);
      usedPattern = 'pattern3';
    }
    
    console.log('📋 Table separator found with', usedPattern, ':', !!match);
    if (match) {
      console.log('📋 Matched separator:', JSON.stringify(match[1]));
    }
    
    if (match) {
      // Create new row - escape any pipe characters in entry
      const safeEntry = entry.replace(/\|/g, '\\|');
      const newRow = `| ${date} | ${version} | ${author} | ${safeEntry} |`;
      console.log('📋 Inserting changelog row:', newRow);
      
      // Insert the new row immediately after the separator
      const separatorIndex = markdown.indexOf(match[1]);
      const beforeSeparator = markdown.substring(0, separatorIndex + match[1].length);
      const afterSeparator = markdown.substring(separatorIndex + match[1].length);
      
      const updated = beforeSeparator + '\n' + newRow + afterSeparator;
      
      console.log('✅ Changelog row inserted at index', separatorIndex);
      return updated;
    }
    
    // Try alternative: look for table after ## Changelog
    const altMatch = markdown.match(changelogSectionRegex);
    if (altMatch) {
      console.log('📋 Found changelog via section heading');
      const safeEntry = entry.replace(/\|/g, '\\|');
      const newRow = `| ${date} | ${version} | ${author} | ${safeEntry} |`;
      
      // Insert after the separator row (second capture group)
      const updated = markdown.replace(
        changelogSectionRegex,
        `$1$2\n${newRow}`
      );
      console.log('✅ Changelog row inserted via section heading method');
      return updated;
    }
    
    console.log('❌ Changelog table not found after all attempts');
    
    return markdown;
  }

  // Publish version function with AI-assisted changelog
  async function publishVersion() {
    if (!currentMeta || !currentFile) return;
    
    // Helper to normalize content for comparison (strips metadata and normalizes formatting)
    function normalizeForComparison(content) {
      if (!content) return '';
      return content
        // Remove CRD-META block
        .replace(/<!--\s*CRD-META[\s\S]*?-->\s*/g, '')
        // Normalize list markers (both - and * to -)
        .replace(/^\* /gm, '- ')
        .replace(/^\*\s+/gm, '- ')
        // Normalize table separators (various dash counts to standard ---)
        .replace(/\|\s*:?-+:?\s*/g, '| --- ')
        // Normalize multiple spaces in tables
        .replace(/\|\s+/g, '| ')
        .replace(/\s+\|/g, ' |')
        // Normalize bold markers
        .replace(/\*\*([^*]+)\*\*/g, '**$1**')
        // Normalize multiple blank lines to single
        .replace(/\n{3,}/g, '\n\n')
        // Normalize trailing whitespace
        .replace(/[ \t]+$/gm, '')
        // Trim
        .trim();
    }
    
    try {
      // Determine version bump type based on newly completed sections
      let newVersion = calculateNewVersion(currentMeta.version, previousSectionStates, currentMeta.sections);
      
      // Check if this is a milestone publish request (from the Ready for Dev button)
      const publishBtn = document.getElementById('publishBtn');
      const isMilestonePublish = publishBtn?.dataset.milestonePublish === 'true';
      
      if (isMilestonePublish) {
        newVersion = '1.0.0';
        console.log('🚀 Milestone publish detected - forcing version to', newVersion);
        // Clear the flag immediately
        delete publishBtn.dataset.milestonePublish;
      }
      
      // Get current editor content as markdown
      let currentContent = getEditorMarkdown();
      
      // Try to fetch the previous version from GitHub for comparison
      let oldContent = '';
      try {
        const response = await fetch(`/api/crd-content?path=${encodeURIComponent(currentFile)}`);
        if (response.ok) {
          const data = await response.json();
          oldContent = data.content;
        }
      } catch (err) {
        console.log('Could not fetch old content for comparison:', err.message);
      }
      
      // Log RAW content before normalization
      console.log('📝 RAW old content (first 500):', oldContent?.substring(0, 500));
      console.log('📝 RAW new content (first 500):', currentContent?.substring(0, 500));
      console.log('📝 RAW old length:', oldContent?.length);
      console.log('📝 RAW new length:', currentContent?.length);
      console.log('📝 RAW content identical:', oldContent === currentContent);
      
      // Normalize both for comparison
      const normalizedOld = normalizeForComparison(oldContent);
      const normalizedNew = normalizeForComparison(currentContent);
      
      console.log('📝 NORMALIZED old (first 500):', normalizedOld?.substring(0, 500));
      console.log('📝 NORMALIZED new (first 500):', normalizedNew?.substring(0, 500));
      console.log('📝 NORMALIZED identical:', normalizedOld === normalizedNew);
      
      console.log('📝 Normalized old (first 300):', normalizedOld?.substring(0, 300));
      console.log('📝 Normalized new (first 300):', normalizedNew?.substring(0, 300));
      
      // Open changelog modal
      const changelogModal = document.getElementById('changelogModal');
      const changelogDate = document.getElementById('changelogDate');
      const changelogVersion = document.getElementById('changelogVersion');
      const changelogAuthor = document.getElementById('changelogAuthor');
      const changelogEntry = document.getElementById('changelogEntry');
      const changelogLoadingHint = document.getElementById('changelogLoadingHint');
      const confirmPublishBtn = document.getElementById('confirmPublish');
      const cancelChangelogBtn = document.getElementById('cancelChangelog');
      const closeChangelogModalBtn = document.getElementById('closeChangelogModal');
      
      // Set date and version
      changelogDate.value = formatUKDate();
      changelogVersion.value = newVersion;
      changelogEntry.value = '';
      
      // Pre-populate author with current user's display name
      const user = getCurrentUser();
      changelogAuthor.value = user?.displayName || user?.username || '';
      
      // Show modal
      changelogModal.style.display = 'flex';
      
      // Generate AI suggestion if we have old content and there are actual changes
      console.log('📝 Old content length:', oldContent?.length);
      console.log('📝 New content length:', currentContent?.length);
      console.log('📝 Normalized content differs:', normalizedOld !== normalizedNew);
      
      if (normalizedOld && normalizedNew && normalizedOld !== normalizedNew) {
        changelogLoadingHint.style.display = 'block';
        changelogEntry.placeholder = 'AI is generating a suggestion...';
        
        // Detect newly completed sections
        const newlyCompleted = [];
        console.log('📋 Previous section states:', previousSectionStates);
        console.log('📋 Current section states:', currentMeta?.sections);
        
        if (previousSectionStates && currentMeta?.sections) {
          for (const [key, status] of Object.entries(currentMeta.sections)) {
            const prevStatus = previousSectionStates[key];
            console.log(`📋 Section "${key}": was "${prevStatus}" → now "${status}"`);
            
            if (status === 'complete' && prevStatus !== 'complete') {
              console.log(`✅ "${key}" is newly complete!`);
              newlyCompleted.push(key);
            }
          }
        }
        
        console.log('📋 Newly completed sections:', newlyCompleted);
        
        // Build section completion context for AI
        let sectionContext = '';
        if (newlyCompleted.length > 0) {
          const sectionNames = {
            requirements: 'Requirements',
            design_specs: 'Design specifications', 
            a11y_approval: 'Accessibility review',
            cms: 'CMS considerations',
            dev_guidance: 'Development guidance',
            docs: 'Documentation',
            checklist: 'Completion checklist'
          };
          
          const completedNames = newlyCompleted.map(key => sectionNames[key] || key);
          sectionContext = `\n\nSECTIONS MARKED COMPLETE: ${completedNames.join(', ')}\nThis is significant and MUST be mentioned in the changelog.`;
        }
        
        // Note: isMilestonePublish is already determined above when checking version
        console.log('🚀 Is milestone publish?', isMilestonePublish);
        
        try {
          console.log('📝 Sending to AI for changelog generation...');
          console.log('📋 Section context being sent:', sectionContext);
          console.log('🎯 Milestone flag:', isMilestonePublish);
          const aiResponse = await apiPost('/api/ai/generate-changelog', {
            oldContent: normalizedOld,
            newContent: normalizedNew,
            version: newVersion,
            sectionContext: sectionContext,
            milestoneReached: isMilestonePublish ? 'ready_for_dev' : null
          });
          
          if (aiResponse.ok) {
            const data = await aiResponse.json();
            console.log('📝 AI changelog received:', data.changelog);
            changelogEntry.value = data.changelog || 'No significant changes detected';
          }
        } catch (err) {
          console.error('AI changelog generation failed:', err);
          changelogEntry.placeholder = 'Describe what changed...';
        } finally {
          changelogLoadingHint.style.display = 'none';
        }
      } else {
        if (!oldContent) {
          console.log('⚠️ No old content available for comparison');
        }
        changelogEntry.placeholder = 'Describe what changed...';
      }
      
      // Handle modal actions
      const handlePublish = async () => {
        const date = changelogDate.value;
        const version = changelogVersion.value;
        const user = getCurrentUser();
        const author = changelogAuthor.value.trim() || user?.displayName || user?.username || 'unknown';
        const entry = changelogEntry.value.trim();
        
        if (!entry) {
          showToast('Please enter a changelog entry', 'error');
          return;
        }
        
        console.log('🚀 Starting publish process for version:', version);
        
        // Update metadata
        currentMeta.version = version;
        
        // Get current content
        let content = getEditorMarkdown();
        const beforeLength = content.length;
        
        console.log('🔍 BEFORE insertChangelogEntry:');
        console.log('📋 Entry text:', entry);
        console.log('📋 Markdown length:', content.length);
        console.log('📋 Markdown contains "Changelog":', content.includes('Changelog'));
        console.log('📋 Markdown contains table separator:', content.includes('| ---'));
        
        // Insert changelog entry
        content = insertChangelogEntry(content, date, version, author, entry);
        
        console.log('🔍 AFTER insertChangelogEntry:');
        console.log('📋 Updated markdown length:', content.length);
        console.log('📋 Length changed:', content.length !== beforeLength);
        
        // Update H1 with minor version (e.g., "0.2" from "0.2.0")
        const minorVersion = version.split('.').slice(0, 2).join('.');
        content = updateH1Version(content, minorVersion);
        
        // Update metadata in content
        content = updateCrdMeta(content, currentMeta);
        
        // Update editor with new content
        updateEditorContent(content);
        
        // Mark as having unsaved changes so save will execute
        hasUnsavedChanges = true;
        
        // Debug: Show what's being saved
        console.log('💾 FINAL MARKDOWN TO SAVE (changelog area):');
        const clIdx = content.indexOf('Changelog');
        if (clIdx > -1) {
          console.log(content.substring(clIdx, clIdx + 500));
        } else {
          console.log('⚠️ No Changelog section found in final markdown!');
        }
        
        try {
          // Save the file
          await saveFile(`Publish v${version}: ${currentFile.split('/').pop()}`);
          
          console.log('✅ File saved successfully');
          
          // Update previous states to current (for next publish)
          previousSectionStates = { ...currentMeta.sections };
          
          // Refresh version badge
          updateVersionBadge();
          
          console.log('🔄 Updating milestone gate after publish, new version:', version);
          
          // Update section nav completion (including milestone gate)
          updateSectionNavCompletion();
          
          console.log('✅ Publish complete, closing modal');
          
          // Close modal and dropdown
          changelogModal.style.display = 'none';
          saveDropdownMenu.classList.remove('open');
          
          // Show success toast
          showToast(`Published v${version}`, 'success');
          
          // Cleanup event listeners
          cleanup();
        } catch (error) {
          console.error('❌ Error during publish save:', error);
          showToast('Failed to save file during publish', 'error');
        }
      };
      
      const handleCancel = () => {
        changelogModal.style.display = 'none';
        cleanup();
      };
      
      const cleanup = () => {
        confirmPublishBtn.removeEventListener('click', handlePublish);
        cancelChangelogBtn.removeEventListener('click', handleCancel);
        closeChangelogModalBtn.removeEventListener('click', handleCancel);
      };
      
      // Attach event listeners
      confirmPublishBtn.addEventListener('click', handlePublish);
      cancelChangelogBtn.addEventListener('click', handleCancel);
      closeChangelogModalBtn.addEventListener('click', handleCancel);
      
    } catch (error) {
      console.error('Error publishing version:', error);
      showToast('Failed to publish version', 'error');
    }
  }
  
  // Publish button handler
  publishBtn?.addEventListener('click', publishVersion);
  
  // Auto-expand requests folder
  // const requestsSection = document.querySelector('[data-folder="requests"]');
  // requestsSection.querySelector('.folder-header').click();
}

// New function to load files into a specific section
async function loadFilesIntoSection(folder, filesDiv) {
  filesDiv.innerHTML = '<div class="loading">Loading...</div>';
  
  try {
    const res = await fetch(`/api/crds/${folder}`);
    const data = await res.json();
    
    let html = '';
    if (folder !== 'archived') {
      html += '<button class="new-crd-btn" data-folder="' + folder + '"><i class="ph ph-plus-circle"></i>New CRD</button>';
    }
    const files = data?.files || [];
    if (files.length === 0) {
      html += '<div class="loading">No files</div>';
    } else {
      // Fetch comment counts for this folder
      let commentCounts = {};
      try {
        const countsRes = await fetch(`/api/comments/counts/${folder}`, {
          credentials: 'include'
        });
        if (countsRes.ok) {
          commentCounts = await countsRes.json();
          console.log('📊 Comment counts loaded:', commentCounts);
        }
      } catch (err) {
        console.error('Failed to fetch comment counts:', err);
      }

      html += files.map(file => {
        const count = commentCounts[file.name] || 0;
        const badge = count > 0 ? '<span class="file-comment-badge">' + count + '</span>' : '';
        return '<div class="file-item" data-path="' + file.path + '" data-name="' + file.name + '"><span class="file-name">' + file.name + '</span>' + badge + '</div>';
      }).join('');
    }
    filesDiv.innerHTML = html;
    
    filesDiv.querySelectorAll('.file-item').forEach(item => {
      item.addEventListener('click', () => {
        if (!item?.dataset) return;
        openFile(item.dataset.path, item.dataset.name);
      });
      
      // Add drag-and-drop for repo owners
      console.log('[loadFilesIntoSection] isRepoOwner check:', isRepoOwner(), 'for file:', item.dataset.name);
      if (isRepoOwner()) {
        console.log('[loadFilesIntoSection] Making draggable:', item.dataset.name);
        item.setAttribute('draggable', 'true');
        item.dataset.folder = folder;
        
        item.addEventListener('dragstart', handleDragStart);
        item.addEventListener('dragend', handleDragEnd);
      }
    });
    
    const newBtn = filesDiv.querySelector('.new-crd-btn');
    if (newBtn) {
      newBtn.addEventListener('click', () => openNewCrdModal(folder));
    }
  } catch (error) {
    filesDiv.innerHTML = '<div class="loading" style="color: red;">Error loading</div>';
  }
}

async function loadFiles(folder) {
  currentFolder = folder;
  document.getElementById('files').innerHTML = '<div class="loading">Loading files...</div>';
  
  try {
    const res = await fetch(`/api/crds/${folder}`);
    const data = await res.json();
    
    const filesDiv = document.getElementById('files');
    const files = data?.files || [];
    
    if (files.length === 0) {
      filesDiv.innerHTML = '<div class="loading">No files in this folder</div>';
    } else {
      filesDiv.innerHTML = files.map(file => `
        <div class="file-item" data-path="${file.path}" data-name="${file.name}">
          ${file.name}
        </div>
      `).join('');
      
      // Add click handlers
      filesDiv.querySelectorAll('.file-item').forEach(item => {
        item.addEventListener('click', () => {
          if (!item?.dataset) return;
          openFile(item.dataset.path, item.dataset.name);
        });
      });
    }
  } catch (error) {
    document.getElementById('files').innerHTML = '<div style="color: red; padding: 16px;">Error loading files</div>';
    console.error('Error loading files:', error.message);
  }
}

function updateSaveStatus(status) {
  const statusEl = document.getElementById('saveStatus');
  const versionBadge = document.getElementById('versionBadge');
  statusEl.className = 'save-status ' + status;
  
  if (status === 'saving') {
    statusEl.textContent = 'Saving...';
    if (versionBadge) versionBadge.style.display = 'none';
  } else if (status === 'saved') {
    statusEl.textContent = 'Saved';
    if (versionBadge) versionBadge.style.display = '';
    setTimeout(() => {
      statusEl.textContent = '';
      statusEl.className = 'save-status';
    }, 2000);
  } else if (status === 'error') {
    statusEl.textContent = 'Save failed';
    if (versionBadge) versionBadge.style.display = '';
  } else {
    statusEl.textContent = '';
    if (versionBadge) versionBadge.style.display = '';
  }
}

function trackEditorChanges() {
  // Check for changes every second
  const checkChanges = () => {
    if (!hasEditor()) return;
    const currentContent = getEditorHTML();
    if (currentContent !== lastSavedContent) {
      if (!hasUnsavedChanges) {
        // Only trigger auto-save scheduling when changes are first detected
        hasUnsavedChanges = true;
        // Schedule auto-save with debounce
        scheduleAutoSaveIfNeeded();
      }
    }
  };
  
  setInterval(checkChanges, 1000);
}

function scheduleAutoSaveIfNeeded() {
  // Only schedule if we have a file open and auto-save isn't already scheduled
  if (!currentFile || autoSaveInterval) return;
  
  // Clear any existing timeout
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
  }
  
  // Wait 3 seconds after last change before first auto-save attempt
  autoSaveTimeout = setTimeout(() => {
    // Then check every 30 seconds
    autoSaveInterval = setInterval(async () => {
      if (hasUnsavedChanges && currentFile) {
        console.log('💾 Auto-saving...');
        updateSaveStatus('saving');
        await saveFile();
      }
    }, 30000); // 30 seconds
    
    // Also do an immediate check after the 3 second delay
    if (hasUnsavedChanges && currentFile) {
      console.log('💾 Auto-saving (after 3s delay)...');
      updateSaveStatus('saving');
      saveFile();
    }
  }, 3000); // Wait 3 seconds after last change
}

async function handleEditorLinkClick(e) {
  const link = e.target.closest('a');
  if (!link) return;
  
  const href = link.getAttribute('href');
  if (!href) return;
  
  // Check if it's a GitHub CRD link (dynamic pattern based on config)
  const config = await loadGitHubConfig();
  const escapedOwner = config.owner.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedRepo = config.repo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedDocsPath = config.docsPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const crdPattern = new RegExp(`github\\.com/${escapedOwner}/${escapedRepo}/blob/[^/]+/${escapedDocsPath}/([^/]+)/(.+\\.md)`);
  const crdMatch = href.match(crdPattern);
  
  if (crdMatch) {
    e.preventDefault();
    const folder = crdMatch[1];  // backlog, requests, active, archived
    const filename = crdMatch[2];  // Button_v1_CRD.md
    const path = config.docsPath + '/' + folder + '/' + filename;
    const displayName = filename.replace('.md', '');
    
    console.log('📎 Opening CRD link in editor:', displayName);
    openFile(path, displayName);
  }
  // If it's not a CRD link, let it open normally in new tab
}

export async function openFile(path, name) {
  console.log('📂 openFile called:', name);
  
  // DEBUG FLAG - set window.DEBUG_FILE_OPEN = true in console to enable detailed logging
  const DEBUG = window.DEBUG_FILE_OPEN || false;
  
  if (DEBUG) console.log('[DEBUG] === START openFile ===');
  
  // Clear section nav immediately to avoid showing stale data
  clearSectionNav();
  
  // Save current file before switching - with safety checks
  if (hasUnsavedChanges && currentFile && currentFileSha && hasEditor()) {
    console.log('💾 Saving before switch...');
    try {
      updateSaveStatus('saving');
      await saveFile();
    } catch (err) {
      console.error('Failed to save before switch:', err);
      // Continue anyway - don't block file loading
    }
  }
  
  // Clear any existing auto-save interval and timeout
  if (autoSaveInterval) {
    clearInterval(autoSaveInterval);
    autoSaveInterval = null;
  }
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
    autoSaveTimeout = null;
  }
  
  // Blur any focused editor elements to prevent bubble menu flash
  document.activeElement?.blur();
  
  currentFile = path;
  
  document.querySelectorAll('.file-item').forEach(item => {
    if (!item) return;
    item.classList.remove('active');
    if (item.dataset?.path === path) {
      item.classList.add('active');
    }
  });
  
  // Only show loading message if there's no editor yet
  // Don't clear the DOM if editor exists - updateEditorContent will handle the switch
  if (!hasEditor()) {
    document.getElementById('editorArea').innerHTML = '<div class="empty-state">Loading file...</div>';
  }
  
  try {
    console.log('🌐 Fetching content from API...');
    const res = await fetch(`/api/crd-content?path=${encodeURIComponent(path)}`);
    console.log('📦 API response received, status:', res.status);
    const data = await res.json();
    console.log('✅ Content parsed');
    console.log('📄 Raw content first 500 chars:', data.content.substring(0, 500));
    
    currentFileSha = data.sha;
    
    // Parse and store CRD metadata
    currentMeta = parseCrdMeta(data.content);
    previousSectionStates = { ...currentMeta.sections };
    console.log('📋 Parsed meta:', currentMeta);
    
    // Reset save button state when loading new file
    window.resetEditorChanges?.();
    document.getElementById('saveBtn')?.classList.remove('has-changes');
    
    if (!hasEditor()) {
      console.log('🆕 First time - creating editor...');
      await initEditor(data.content);
      console.log('✅ Editor created successfully');
      
      // Attach click handler to intercept CRD links
      document.querySelector('.ProseMirror').addEventListener('click', handleEditorLinkClick);
    } else {
      console.log('♻️ Updating existing editor...');
      updateEditorContent(data.content);
      console.log('✅ Editor updated');
    }
    
    // Restore comment highlights after editor content is loaded
    setTimeout(() => {
      restoreCommentHighlights();
    }, 100);
    
    console.log('🎨 Setting UI elements...');
    
    // ========== DEBUG: UI VISIBILITY TRACKING ==========
    const editorArea = document.getElementById('editorArea');
    const editorContainer = document.getElementById('editorContainer');
    const sectionNavBar = document.getElementById('sectionNavBar');
    const fileNameDisplay = document.getElementById('fileNameDisplay');
    const saveDropdown = document.getElementById('saveDropdown');
    const deleteBtn = document.getElementById('deleteBtn');
    
    if (DEBUG) {
      console.log('[DEBUG] === UI ELEMENT VISIBILITY CHECK - BEFORE CHANGES ===');
      console.log('[DEBUG] editorArea:', {
        id: 'editorArea',
        exists: !!editorArea,
        display: editorArea ? window.getComputedStyle(editorArea).display : 'NOT FOUND',
        visibility: editorArea ? window.getComputedStyle(editorArea).visibility : 'NOT FOUND',
        opacity: editorArea ? window.getComputedStyle(editorArea).opacity : 'NOT FOUND',
        innerHTML_length: editorArea?.innerHTML?.length || 0,
        classes: editorArea?.className || 'none'
      });
      console.log('[DEBUG] editorContainer:', {
        id: 'editorContainer',
        exists: !!editorContainer,
        display: editorContainer ? window.getComputedStyle(editorContainer).display : 'NOT FOUND',
        visibility: editorContainer ? window.getComputedStyle(editorContainer).visibility : 'NOT FOUND',
        opacity: editorContainer ? window.getComputedStyle(editorContainer).opacity : 'NOT FOUND',
        classes: editorContainer?.className || 'none'
      });
      console.log('[DEBUG] sectionNavBar:', {
        id: 'sectionNavBar',
        exists: !!sectionNavBar,
        display_style: sectionNavBar?.style.display || 'NOT SET',
        display_computed: sectionNavBar ? window.getComputedStyle(sectionNavBar).display : 'NOT FOUND',
        classes: sectionNavBar?.className || 'none'
      });
      console.log('[DEBUG] Content checks:', {
        hasEmptyState: editorArea?.querySelector('.empty-state') !== null,
        hasTiptapEditor: editorArea?.querySelector('#tiptapEditor') !== null,
        hasProseMirror: editorArea?.querySelector('.ProseMirror') !== null
      });
    }
    
    // Log DOM state for debugging UI visibility issues (non-DEBUG mode)
    if (!DEBUG) {
      console.log('📊 DOM State Check:', {
        editorArea: {
          exists: !!editorArea,
          innerHTML: editorArea?.innerHTML?.substring(0, 100),
          display: editorArea ? window.getComputedStyle(editorArea).display : 'N/A',
          visibility: editorArea ? window.getComputedStyle(editorArea).visibility : 'N/A'
        },
        editorContainer: {
          exists: !!editorContainer,
          display: editorContainer ? window.getComputedStyle(editorContainer).display : 'N/A',
          visibility: editorContainer ? window.getComputedStyle(editorContainer).visibility : 'N/A'
        },
        sectionNavBar: {
          exists: !!sectionNavBar,
          display: sectionNavBar ? window.getComputedStyle(sectionNavBar).display : 'N/A'
        },
        hasEmptyState: editorArea?.querySelector('.empty-state') !== null,
        hasTiptapEditor: editorArea?.querySelector('#tiptapEditor') !== null,
        hasProseMirror: editorArea?.querySelector('.ProseMirror') !== null
      });
    }
    
    // Now make the UI changes
    if (DEBUG) console.log('[DEBUG] === MAKING UI CHANGES ===');
    
    if (fileNameDisplay) {
      if (DEBUG) console.log('[DEBUG] fileNameDisplay - BEFORE:', fileNameDisplay.style.display);
      fileNameDisplay.style.display = 'block';
      if (DEBUG) console.log('[DEBUG] fileNameDisplay - AFTER:', window.getComputedStyle(fileNameDisplay).display);
    }
    
    const fileNameEl = document.getElementById('fileName');
    if (fileNameEl) {
      fileNameEl.textContent = name;
      if (DEBUG) console.log('[DEBUG] fileName text set to:', name);
    }
    
    if (saveDropdown) {
      if (DEBUG) console.log('[DEBUG] saveDropdown - BEFORE:', saveDropdown.style.display);
      saveDropdown.style.display = 'flex';
      if (DEBUG) console.log('[DEBUG] saveDropdown - AFTER:', window.getComputedStyle(saveDropdown).display);
    }
    
    if (deleteBtn) {
      if (DEBUG) console.log('[DEBUG] deleteBtn - BEFORE:', deleteBtn.style.display);
      deleteBtn.style.display = 'flex';
      if (DEBUG) console.log('[DEBUG] deleteBtn - AFTER:', window.getComputedStyle(deleteBtn).display);
    }
    
    if (DEBUG) {
      console.log('[DEBUG] === UI ELEMENT VISIBILITY CHECK - AFTER CHANGES ===');
      console.log('[DEBUG] editorArea:', {
        display: editorArea ? window.getComputedStyle(editorArea).display : 'NOT FOUND',
        visibility: editorArea ? window.getComputedStyle(editorArea).visibility : 'NOT FOUND',
        opacity: editorArea ? window.getComputedStyle(editorArea).opacity : 'NOT FOUND'
      });
      console.log('[DEBUG] editorContainer:', {
        display: editorContainer ? window.getComputedStyle(editorContainer).display : 'NOT FOUND',
        visibility: editorContainer ? window.getComputedStyle(editorContainer).visibility : 'NOT FOUND',
        opacity: editorContainer ? window.getComputedStyle(editorContainer).opacity : 'NOT FOUND'
      });
    }
    
    console.log('✅ File loaded successfully!');
    
    // Load comments for this file
    loadComments(currentFile);
    
    // Update AI chat context
    const config = await loadGitHubConfig();
    const pathParts = path.split('/');
    const docsPathParts = config.docsPath.split('/');
    const folderIndex = docsPathParts.length; // Folder comes after docsPath
    const folder = pathParts[folderIndex]; // Extract folder from path
    console.log('🔍 Updating AI context:', { folder, filename: name });
    updateAIChatContext(folder, name);
    
    // Update section navigation and version badge (slight delay for DOM to update)
    setTimeout(() => {
      if (DEBUG) console.log('[DEBUG] === INSIDE setTimeout (100ms) - BEFORE updateSectionNav ===');
      
      updateSectionNav();
      updateVersionBadge();
      
      if (DEBUG) {
        console.log('[DEBUG] === INSIDE setTimeout (100ms) - AFTER updateSectionNav ===');
        const ea = document.getElementById('editorArea');
        const ec = document.getElementById('editorContainer');
        console.log('[DEBUG] editorArea display:', ea ? window.getComputedStyle(ea).display : 'NOT FOUND');
        console.log('[DEBUG] editorContainer display:', ec ? window.getComputedStyle(ec).display : 'NOT FOUND');
      }
      
      // Initialize section completion tracking
      initSectionComplete(currentMeta, (updatedMeta) => {
        // Callback when metadata changes from checkbox toggles
        currentMeta = updatedMeta;
        hasUnsavedChanges = true;
        updateSaveStatus('unsaved');
        
        // Refresh section nav to show updated completion status
        updateSectionNav();
      });
      
      if (DEBUG) {
        console.log('[DEBUG] === INSIDE setTimeout (100ms) - AFTER initSectionComplete ===');
        const ea = document.getElementById('editorArea');
        const ec = document.getElementById('editorContainer');
        console.log('[DEBUG] editorArea display:', ea ? window.getComputedStyle(ea).display : 'NOT FOUND');
        console.log('[DEBUG] editorContainer display:', ec ? window.getComputedStyle(ec).display : 'NOT FOUND');
      }
    }, 100);
    
    // Track saved content and start auto-save
    lastSavedContent = getEditorHTML();
    hasUnsavedChanges = false;
    
    // Start change tracking if not already started
    if (!window.changeTrackingStarted) {
      trackEditorChanges();
      window.changeTrackingStarted = true;
    }
    
    // Clear existing interval and timeout
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
      autoSaveInterval = null;
    }
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
      autoSaveTimeout = null;
    }
    
    // Note: Auto-save will be scheduled by scheduleAutoSaveIfNeeded() 
    // when changes are detected, not immediately when file opens
    
    // DEBUG: Check TipTap output
    setTimeout(() => {
      console.log('=== CHECKING TIPTAP OUTPUT ===');
      const editorHTML = getEditorHTML();
      console.log('Editor HTML:', editorHTML);
      console.log('ProseMirror DOM:', document.querySelector('.ProseMirror')?.innerHTML);
      
      // Specifically check for task lists
      const taskLists = document.querySelectorAll('[data-type="taskList"]');
      const taskItems = document.querySelectorAll('[data-type="taskItem"]');
      console.log('Found task lists:', taskLists.length);
      console.log('Found task items:', taskItems.length);
      
      if (taskItems.length > 0) {
        console.log('First task item HTML:', taskItems[0].outerHTML);
      }
      
      if (DEBUG) {
        console.log('[DEBUG] === INSIDE setTimeout (1000ms) - FINAL VISIBILITY CHECK ===');
        const ea = document.getElementById('editorArea');
        const ec = document.getElementById('editorContainer');
        const snb = document.getElementById('sectionNavBar');
        console.log('[DEBUG] editorArea:', {
          display: ea ? window.getComputedStyle(ea).display : 'NOT FOUND',
          visibility: ea ? window.getComputedStyle(ea).visibility : 'NOT FOUND',
          opacity: ea ? window.getComputedStyle(ea).opacity : 'NOT FOUND',
          offsetWidth: ea?.offsetWidth || 0,
          offsetHeight: ea?.offsetHeight || 0
        });
        console.log('[DEBUG] editorContainer:', {
          display: ec ? window.getComputedStyle(ec).display : 'NOT FOUND',
          visibility: ec ? window.getComputedStyle(ec).visibility : 'NOT FOUND',
          opacity: ec ? window.getComputedStyle(ec).opacity : 'NOT FOUND',
          offsetWidth: ec?.offsetWidth || 0,
          offsetHeight: ec?.offsetHeight || 0
        });
        console.log('[DEBUG] sectionNavBar:', {
          display: snb ? window.getComputedStyle(snb).display : 'NOT FOUND',
          visibility: snb ? window.getComputedStyle(snb).visibility : 'NOT FOUND'
        });
      }
    }, 1000);
    
    // Final check at the end of openFile function
    if (DEBUG) {
      console.log('[DEBUG] === END openFile (synchronous) - FINAL STATE ===');
      const ea = document.getElementById('editorArea');
      const ec = document.getElementById('editorContainer');
      console.log('[DEBUG] openFile complete, editorArea display is:', ea ? window.getComputedStyle(ea).display : 'NOT FOUND');
      console.log('[DEBUG] openFile complete, editorContainer display is:', ec ? window.getComputedStyle(ec).display : 'NOT FOUND');
      console.log('[DEBUG] === END openFile ===');
    }
    
  } catch (error) {
    console.error('❌ Error in openFile:', error);
    document.getElementById('editorArea').innerHTML = '<div class="empty-state" style="color: red;">Error loading file: ' + error.message + '</div>';
    updateSaveStatus('error');
    
    // Clear state on error
    currentFile = null;
    currentFileSha = null;
    currentMeta = null;
    clearSectionNav();
    clearComments();
  }
}

async function saveFile(commitMessage = null) {
  if (!hasEditor()) {
    console.error('❌ SAVE ERROR: No editor found');
    return;
  }
  
  // Ensure commitMessage is a string or null, not an event object
  if (commitMessage && typeof commitMessage !== 'string') {
    console.warn('⚠️ Invalid commitMessage type received:', typeof commitMessage, commitMessage);
    commitMessage = null;
  }
  
  const saveBtn = document.getElementById('saveBtn');
  saveBtn.innerHTML = '<i class="ph ph-floppy-disk"></i> Saving...';
  saveBtn.disabled = true;
  
  try {
    let markdown = getEditorMarkdown();
    
    console.log('💾 ===== SAVE FILE START =====');
    console.log('💾 About to save file:', currentFile);
    console.log('💾 Content length:', markdown.length);
    console.log('💾 Current SHA:', currentFileSha);
    console.log('💾 Current meta version:', currentMeta?.version);
    console.log('💾 Commit message:', commitMessage);
    
    console.log('💾 SAVE - Raw markdown from editor (checkboxes area):');
    console.log(markdown.substring(4000, 5000));
    console.log('💾 SAVE - Searching for checkbox patterns:');
    console.log('  - [ ] patterns:', markdown.match(/-\s*\[\s*\]/g));
    console.log('  - [x] patterns:', markdown.match(/-\s*\[[xX]\]/g));
    
    // Ensure CRD metadata is embedded in the content
    if (currentMeta) {
      markdown = updateCrdMeta(markdown, currentMeta);
      console.log('💾 SAVE - After updateCrdMeta (checkboxes area):');
      console.log(markdown.substring(4000, 5000));
      console.log('  - [ ] patterns:', markdown.match(/-\s*\[\s*\]/g));
      console.log('  - [x] patterns:', markdown.match(/-\s*\[[xX]\]/g));
    }
    
    console.log('💾 Making API call to /api/crd-content...');
    
    // Build request body - only include message if it's a valid string
    const requestBody = {
      path: currentFile,
      content: markdown,
      sha: currentFileSha
    };
    
    if (commitMessage && typeof commitMessage === 'string') {
      requestBody.message = commitMessage;
    }
    
    const res = await apiPut('/api/crd-content', requestBody);
    
    console.log('💾 API Response status:', res.status, res.statusText);
    
    if (!res.ok) {
      let errorData;
      try {
        errorData = await res.json();
      } catch (e) {
        const errorText = await res.text();
        console.error('❌ API response not OK:', errorText);
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }
      
      // Handle SHA mismatch / conflict error
      if (res.status === 409 && errorData.conflict) {
        console.error('⚠️ SHA mismatch detected - file was modified on GitHub');
        const shouldReload = confirm(
          'The file has been modified on GitHub since you opened it.\n\n' +
          'Click OK to reload the latest version (your unsaved changes will be lost).\n' +
          'Click Cancel to keep your current version and try saving again later.'
        );
        
        if (shouldReload) {
          // Reload the file
          if (currentFile) {
            const fileName = currentFile.split('/').pop();
            await openFile(currentFile, fileName);
            alert('File reloaded. Your unsaved changes were lost. Please make your changes again.');
          }
          return; // Don't throw error, just return
        } else {
          // Update SHA and allow user to try again
          if (errorData.currentSha) {
            currentFileSha = errorData.currentSha;
            console.log('📋 Updated SHA to:', currentFileSha);
            alert('SHA updated. You can try saving again, but be aware the file may have changed on GitHub.');
          }
          throw new Error(errorData.message || 'File conflict - please reload and try again');
        }
      }
      
      console.error('❌ API response not OK:', errorData);
      throw new Error(errorData.error || `HTTP ${res.status}: ${JSON.stringify(errorData)}`);
    }
    
    const data = await res.json();
    console.log('📦 Save response data:', data);
    
    if (data.success) {
      console.log('✅ Save successful!');
      console.log('📋 Old SHA:', currentFileSha);
      console.log('📋 New SHA from response:', data.sha);
      currentFileSha = data.sha;
      console.log('📋 Current SHA after update:', currentFileSha);
      lastSavedContent = getEditorHTML();
      hasUnsavedChanges = false;
      updateSaveStatus('saved');
      window.resetEditorChanges?.();
      saveBtn.innerHTML = '<i class="ph ph-floppy-disk"></i> Quick save';
      saveBtn.disabled = false;
      console.log('💾 ===== SAVE FILE COMPLETE =====');
    } else {
      console.error('❌ Save response indicated failure:', data.error);
      throw new Error(data.error || 'Unknown save error');
    }
  } catch (error) {
    console.error('❌ ===== SAVE FILE ERROR =====');
    console.error('❌ Save error type:', error.constructor.name);
    console.error('❌ Save error message:', error.message);
    console.error('❌ Save error stack:', error.stack);
    console.error('❌ Full error object:', error);
    updateSaveStatus('error');
    alert('Save failed: ' + error.message);
    saveBtn.innerHTML = '<i class="ph ph-floppy-disk"></i> Quick save';
    saveBtn.disabled = false;
    throw error; // Re-throw so the publish handler can catch it
  }
}

function confirmDelete() {
  if (!currentFile) return;
  
  const modal = document.getElementById('deleteModal');
  if (!modal) return;
  const fileName = document.getElementById('fileName').textContent;
  
  modal.dataset.path = currentFile;
  modal.dataset.sha = currentFileSha;
  modal.dataset.fileName = fileName;
  
  document.getElementById('deleteFileNameDisplay').textContent = fileName;
  document.getElementById('deleteConfirmInput').value = '';
  document.getElementById('confirmDeleteBtn').disabled = true;
  
  modal.classList.add('active');
  setTimeout(() => document.getElementById('deleteConfirmInput').focus(), 100);
}

function closeDeleteModal() {
  document.getElementById('deleteModal').classList.remove('active');
}

async function deleteFile() {
  const modal = document.getElementById('deleteModal');
  if (!modal?.dataset) return;
  const path = modal.dataset.path;
  const sha = modal.dataset.sha;
  const fileName = modal.dataset.fileName;
  
  const deleteBtn = document.getElementById('confirmDeleteBtn');
  deleteBtn.disabled = true;
  deleteBtn.textContent = 'Deleting...';
  
  try {
    const res = await apiDelete('/api/delete-crd', { path, sha, message: 'Delete ' + fileName });
    
    const data = await res.json();
    
    if (data.success) {
      closeDeleteModal();
      
      // Extract folder and filename from the deleted path
      const pathParts = path.split('/');
      const deletedFolder = pathParts[2];  // e.g., 'requests', 'backlog', etc.
      const deletedFilename = pathParts[3]; // e.g., 'Button_CRD.md'
      
      // Refresh the file list for the folder
      const section = document.querySelector('[data-folder="' + deletedFolder + '"]');
      const filesDiv = section.querySelector('.folder-files');
      await loadFilesIntoSection(deletedFolder, filesDiv);
      
      // Check if we just deleted the currently open file
      const currentCRD = await getCurrentCRD();
      if (currentCRD && currentCRD.folder === deletedFolder && currentCRD.filename === deletedFilename) {
        console.log('🗑️ Deleted file was open, returning to welcome state');
        
        // Clear current file state
        currentFile = null;
        currentFileSha = null;
        currentMeta = null;
        lastSavedContent = '';
        hasUnsavedChanges = false;
        clearSectionComplete();
        
        // Clear any existing auto-save interval and timeout
        if (autoSaveInterval) {
          clearInterval(autoSaveInterval);
          autoSaveInterval = null;
        }
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
          autoSaveTimeout = null;
        }
        
        // Destroy the editor instance if it exists
        const editor = getEditor();
        if (editor) {
          editor.destroy();
        }
        
        // Restore the original empty state HTML
        const editorArea = document.getElementById('editorArea');
        if (editorArea) {
          editorArea.innerHTML = '<div class="empty-state">Select a file to edit</div>';
        }
        
        // Hide file info and buttons
        document.getElementById('fileNameDisplay').style.display = 'none';
        document.getElementById('saveDropdown').style.display = 'none';
        document.getElementById('deleteBtn').style.display = 'none';
        
        // Update AI chat context to no file
        updateAIChatContext(null, null);
        
        console.log('✅ Returned to welcome state');
      }
    } else {
      alert('Error deleting file: ' + data.error);
    }
  } catch (error) {
    alert('Error: ' + error.message);
  } finally {
    deleteBtn.disabled = false;
    deleteBtn.textContent = 'Delete';
  }
}

export function getCurrentFile() {
  return currentFile;
}

export async function getCurrentCRD() {
  if (!currentFile) {
    return null;
  }
  
  // Parse path to extract folder and filename
  // Path format: "{docsPath}/{folder}/{filename}"
  const config = await loadGitHubConfig();
  const parts = currentFile.split('/');
  const docsPathParts = config.docsPath.split('/');
  const folderIndex = docsPathParts.length;
  
  if (parts.length >= folderIndex + 2) {
    // Verify the path starts with docsPath
    const pathPrefix = parts.slice(0, folderIndex).join('/');
    if (pathPrefix === config.docsPath) {
      return {
        folder: parts[folderIndex],      // e.g., "backlog"
        filename: parts[folderIndex + 1] // e.g., "Card_CRD.md"
      };
    }
  }
  
  return null;
}

export function getCurrentMeta() {
  return currentMeta;
}

// Listen for CRD link clicks from editor
document.addEventListener('loadCrdFile', async (e) => {
  const { path } = e.detail;
  
  // Parse the path to extract folder and filename
  // Format: crds/folder/filename.md
  const parts = path.split('/');
  const folder = parts[1];
  const filename = parts[2];
  
  // Check if we need to save current file first
  if (currentFile) {
    const confirmLeave = confirm('You have unsaved changes. Do you want to save before opening another file?');
    if (confirmLeave) {
      await saveFile();
    }
  }
  
  // Load the file
  await loadFile(folder, filename.replace('.md', ''), path);
});

async function openNewCrdModal(folder) {
  const modal = document.getElementById('newCrdModal');
  if (!modal) return;
  modal.dataset.folder = folder;
  document.getElementById('componentName').value = '';
  document.querySelectorAll('.segment-btn').forEach(b => b.classList.remove('active'));
  document.querySelector('.segment-btn[data-type="new"]').classList.add('active');
  document.getElementById('newComponentFields').style.display = 'block';
  document.getElementById('amendmentFields').style.display = 'none';
  await loadExistingComponents();
  modal.classList.add('active');
  setTimeout(() => document.getElementById('componentName').focus(), 100);
}

async function loadExistingComponents() {
  try {
    const res = await fetch('/api/all-crds');
    const data = await res.json();
    const components = new Set();
    (data?.crds || []).forEach(crd => {
      const match = crd.name.match(/^(.+?)(?:_A\d+)?_CRD$/);
      if (match) components.add(match[1]);
    });
    const select = document.getElementById('existingComponent');
    select.innerHTML = Array.from(components).sort().map(c => '<option value="' + c + '">' + c + '</option>').join('');
    updateAmendmentPreview();
  } catch (err) {
    console.error('Failed to load components:', err);
  }
}

function closeNewCrdModal() {
  document.getElementById('newCrdModal').classList.remove('active');
}

function closeFile() {
  // Stop auto-save
  if (autoSaveInterval) {
    clearInterval(autoSaveInterval);
    autoSaveInterval = null;
  }
  if (autoSaveTimeout) {
    clearTimeout(autoSaveTimeout);
    autoSaveTimeout = null;
  }
  
  // Reset state variables
  currentFile = null;
  currentFileSha = null;
  currentMeta = null;
  previousSectionStates = null;
  hasUnsavedChanges = false;
  lastSavedContent = '';
  
  // Clear UI elements
  clearSectionNav();
  clearSectionComplete();
  clearComments();
  
  // Clear editor content
  if (hasEditor()) {
    const editor = getEditor();
    if (editor) {
      editor.commands.setContent('');
    }
  }
  
  // Update toolbar header to show no file open
  const toolbarHeader = document.querySelector('.toolbar-header h2');
  if (toolbarHeader) {
    toolbarHeader.textContent = 'No file open';
  }
  
  // Hide save/delete buttons, show welcome state
  const saveBtn = document.getElementById('saveBtn');
  const deleteBtn = document.getElementById('deleteBtn');
  const saveDropdownToggle = document.getElementById('saveDropdownToggle');
  
  if (saveBtn) saveBtn.style.display = 'none';
  if (deleteBtn) deleteBtn.style.display = 'none';
  if (saveDropdownToggle) saveDropdownToggle.style.display = 'none';
  
  // Clear version badge
  updateVersionBadge('');
  
  console.log('📁 File closed');
}

async function getNextAmendmentNumber(componentName, folder) {
  try {
    const res = await fetch('/api/crds/' + folder);
    const data = await res.json();
    const files = data?.files || [];
    
    // Filter for amendment files: ComponentName_A#_CRD.md
    const amendments = files.filter(f => 
      f.name.startsWith(componentName + '_A') && 
      f.name.endsWith('_CRD.md')
    );
    
    // Extract numbers from filenames
    const numbers = amendments.map(f => {
      const match = f.name.match(/_A(\d+)_CRD\.md$/);
      return match ? parseInt(match[1]) : 0;
    });
    
    // Return next number (max + 1, or 1 if no amendments exist)
    const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;
    return maxNum + 1;
  } catch (err) {
    console.error('Error getting next amendment number:', err);
    return 1;
  }
}

async function updateAmendmentPreview() {
  const component = document.getElementById('existingComponent').value;
  const modal = document.getElementById('newCrdModal');
  if (!modal?.dataset?.folder) return;
  const folder = modal.dataset.folder;
  const nextNum = await getNextAmendmentNumber(component, folder);
  document.getElementById('amendmentPreview').textContent = 'Will create: ' + component + '_A' + nextNum + '_CRD.md';
}

async function createNewCrd() {
  const modal = document.getElementById('newCrdModal');
  if (!modal?.dataset?.folder) return;
  const folder = modal.dataset.folder;
  const activeBtn = document.querySelector('.segment-btn.active');
  if (!activeBtn?.dataset?.type) return;
  const type = activeBtn.dataset.type;
  let filename, componentName, template;
  
  if (type === 'new') {
    componentName = document.getElementById('componentName').value.trim();
    if (!componentName) { alert('Please enter a component name'); return; }
    filename = componentName.replace(/\s+/g, '_') + '_CRD.md';
    
    // NEW COMPONENT TEMPLATE
    template = `<!-- CRD-META
version: 0.1.0
sections:
  requirements: not_started
  design_specs: not_started
  a11y_approval: not_started
  cms: not_started
  dev_guidance: not_started
  docs: not_started
  checklist: not_started
-->

# ${componentName} CRD v0.1

## Changelog

| Date | Vers | Author | Entry |
| --- | --- | --- | --- |
|  | 0.1 |  | Draft requirements |
|  |  |  |  |

---

# REQUIREMENTS

## Purpose

Why this component exists and what problem it solves.

## Scope

**In scope:** What this component does

**Out of scope:** What it explicitly doesn't do

## Requirements

#### Visual variants

- [List hierarchy/style variations anticipated]

#### Sizes

- [Anticipated size options]

#### Icons/Media

- [Icon support, image placement, etc.]

#### Functionality

- [Key capabilities required]

#### Content

- [Key considerations, e.g. limits, types of content it should hold, etc.]

#### Misc

- [Any other component specific requirements.]

## States

- [Default, hover, focus, active, disabled, loading, error, success, etc.]

## Behaviour

#### Interactions

- [Click/tap/drag behaviour, keyboard navigation, focus management]

#### Responsive

- [Mobile/tablet/desktop considerations, breakpoint behaviour]

#### Animation/Transitions

- [What should animate, timing requirements]

## Accessibility

- [ARIA roles/labels/attributes required]
- [Keyboard shortcuts and navigation patterns]
- [Screen reader announcements needed]
- [Focus management requirements]
- **Colour contrast:** Design must identify all colour pairings (text/background, icon/surface, etc.) and ensure they meet WCAG 2.1 AA minimum (4.5:1 for normal text, 3:1 for large text and UI components)

<!-- list break -->

- [ ] Accessibility review complete

## Integration

#### Related components

- [How this differs from or complements existing components]

#### Composition

- [Child components it should accept]
- [Parent components it needs to work within]
- [Other composition or relationship rules]

## Technical Constraints

- [Browser/device support requirements - typically project-wide standards]
- [Performance requirements - typically project-wide standards]
- [Bundle size targets - typically project-wide standards]

## Open Questions

- [Unresolved decisions]

## Notes

- [Design constraints, brand guidelines, or other considerations]

<!-- list break -->

- [ ] Requirements section complete

---

# DESIGN SPECIFICATIONS

## Properties in Figma

| Property Name | Type | Default | Variant Options | Description |
| --- | --- | --- | --- | --- |
|  |  |  |  |  |

## Code properties

*These properties are proposed based on design requirements. Development team should review for technical implementation considerations and suggest refinements to the API as needed.*

| Property Name | Type | Required | Default | Description |
| --- | --- | --- | --- | --- |
|  |  |  |  |  |

## Figma/Code mapping

| Figma Property | Code Property | Mapping Logic | Notes |
| --- | --- | --- | --- |
|  |  |  |  |

## Colour Contrast Pairings

[Pairings that require validation to meet WCAG 2.1 AA minimum]

- [e.g., Primary button text on primary button background: 4.5:1]
- [Icon on button background: 3:1]

## Design Assets

- Figma: [link to component]

<!-- list break -->

- [ ] Design specifications section complete

---

# CMS BEHAVIOUR

This section defines how the component behaves when a self-serve user adds it to a page in the CMS, including layout constraints and which properties they can configure directly.

#### Layout constraints

- Minimum width: [e.g., 120px / 2 columns]
- Maximum width: [e.g., full-width / 12 columns]
- Width behaviour: [e.g., hug content | fill container | fixed options]
- Responsive notes: [e.g., stacks vertically below tablet breakpoint, collapses to icon-only on mobile]

#### Exposed properties

| Property | Options | Default | Notes |
| --- | --- | --- | --- |
| [e.g., Variant] | [Primary, Secondary, Tertiary] | Primary |  |
| [e.g., Size] | [Regular, Small] | Regular |  |

#### Guardrails

- [e.g., Label is required; cannot be empty]
- [e.g., If icon-only, tooltip text is mandatory]

<!-- list break -->

- [ ] CMS theming considerations section complete

---

# DEVELOPMENT GUIDANCE

## Technical Implementation Notes

- [Specific patterns or architecture to follow]
- [Required libraries or utilities]
- [Complex behaviour that needs extra attention]
- [Accessibility implementation requirements]

## Known Constraints

- [Technical limitations to be aware of]
- [Browser/device specific considerations]
- [Performance considerations for implementation]

## Edge Cases

- [Unusual states or scenarios to handle]
- [Error conditions and fallbacks]

<!-- list break -->

- [ ] Development guidance section complete

---

# DOCUMENTATION GUIDANCE

## Design Notes

[Information needed to write final documentation - edge cases, usage guidance, gotchas, migration considerations]

## Implementation Insights

- [Actual limitations discovered during development]
- [Performance characteristics and thresholds]
- [Browser-specific quirks or workarounds]
- [Common mistakes encountered during implementation]

## Usage Context

- [Real-world use cases and scenarios]
- [When to use this vs. related components]
- [Important patterns or behaviours to highlight]
- [Gotchas users should be aware of]

## Migration & Changes

- [If replacing existing component - what changed and why]
- [Breaking changes or deviations from original design]
- [Future considerations or known limitations]

<!-- list break -->

- [ ] Documentation guidance section complete

---

# COMPLETION CHECKLIST

## Acceptance Criteria

#### Quality Standards

- [ ] Component built with semantic T3 design tokens (no hard-coded values)
- [ ] Tested across all brand themes in Figma
- [ ] All Figma-tested colour pairings meet WCAG 2.1 AA contrast requirements
- [ ] Runtime accessibility contrast pairings defined
- [ ] Passes accessibility audit (WCAG 2.1 AA minimum)
- [ ] Unit tests cover all variants and states
- [ ] Performance benchmarks met
- [ ] All requirements from Requirements section implemented
- [ ] *Add component specific criteria*

#### AI Readiness

- [ ] Design: Layer naming follows semantic conventions
- [ ] Design: Component and property names are descriptive and unambiguous
- [ ] Dev: Component metadata available as structured data (JSON/API)
- [ ] Dev: Props use semantic, parseable naming
- [ ] Documentation: Component relationships mapped and documented
- [ ] Documentation: Structured format with searchable metadata
- [ ] *Add component specific criteria*

<!-- list break -->

- [ ] Completion checklist section complete
`;
    
  } else {
    // AMENDMENT
    componentName = document.getElementById('existingComponent').value;
    
    // Fetch existing amendments FIRST to get the next number
    let existingAmendments = [];
    try {
      const res = await fetch('/api/crds/' + folder);
      const data = await res.json();
      const files = data?.files || [];
      existingAmendments = files
        .filter(f => f.name.startsWith(componentName + '_A') && f.name.endsWith('_CRD.md'))
        .sort((a, b) => {
          const numA = parseInt(a.name.match(/_A(\d+)_CRD\.md$/)[1]);
          const numB = parseInt(b.name.match(/_A(\d+)_CRD\.md$/)[1]);
          return numA - numB;
        });
    } catch (err) {
      console.error('Failed to fetch amendments:', err);
    }
    
    // Calculate next amendment number
    const amendNum = existingAmendments.length > 0
      ? parseInt(existingAmendments[existingAmendments.length - 1].name.match(/_A(\d+)_CRD\.md$/)[1]) + 1
      : 1;
    
    filename = componentName + '_A' + amendNum + '_CRD.md';
    const originalFilename = componentName + '_CRD.md';
    
    // Load config for GitHub URLs (needed for both original and previous amendments links)
    const config = await loadGitHubConfig();
    
    // Generate previous amendments list with full GitHub URLs
    let previousAmendmentsList;
    if (existingAmendments.length === 0) {
      previousAmendmentsList = '- **Previous amendments:** None';
    } else {
      const links = existingAmendments.map(a => {
        const num = a.name.match(/_A(\d+)_CRD\.md$/)[1];
        return `  - [${componentName} A${num} CRD](https://github.com/${config.owner}/${config.repo}/blob/${config.branch}/${config.docsPath}/${folder}/${a.name})`;
      }).join('\n');
      previousAmendmentsList = '- **Previous amendments:**\n' + links;
    }
    
    // AMENDMENT TEMPLATE with full GitHub URLs
    template = `<!-- CRD-META
version: 0.1.0
sections:
  requirements: not_started
  design_specs: not_started
  a11y_approval: not_started
  cms: not_started
  dev_guidance: not_started
  docs: not_started
  checklist: not_started
-->

# ${componentName} A${amendNum} CRD v0.1

## Changelog

| Date | Vers | Author | Entry |
| --- | --- | --- | --- |
|  | 0.1 |  | Amendment requirements defined |
|  |  |  |  |

---

## RELATED CRDS

- **Original:** [${componentName} CRD](https://github.com/${config.owner}/${config.repo}/blob/${config.branch}/${config.docsPath}/${folder}/${originalFilename})
${previousAmendmentsList}

---

# AMENDMENT OVERVIEW

## What's Changing

[Brief summary of what this amendment adds/changes/removes]

## Why This Change

[Business/user need driving this amendment]

## Impact Assessment

- **Breaking changes:** [Yes/No - describe if yes]
- **Affected variants:** [Which existing variants are impacted]
- **New functionality:** [What's being added]
- **Deprecated:** [What's being removed/deprecated]

---

# REQUIREMENTS

*Only document CHANGED or NEW requirements - don't repeat existing functionality from the original CRD.*

## New/Modified Visual Variants

- [Only list variants being added or changed]

## New/Modified Sizes

- [Only list sizes being added or changed]

## New/Modified States

- [Only list states being added or changed]

## New/Modified Behaviour

#### Interactions

- [Only changed/new interactions]

#### Responsive

- [Only changed/new responsive behaviour]

#### Animation/Transitions

- [Only changed/new animations]

## Accessibility Considerations

- [Any new ARIA requirements]
- [Any changed keyboard patterns]
- [New colour contrast pairings to validate]
- [Screen reader announcement changes]

<!-- list break -->

- [ ] Accessibility review complete

## Integration Changes

- [How this affects relationships with other components]
- [New composition rules or constraints]

## Technical Constraints

- [Any new constraints introduced by this change]
- [Compatibility considerations with existing implementations]
- [Migration path complexity]

## Open Questions

- [Unresolved decisions specific to this amendment]

## Notes

- [Important context about why these specific changes]

<!-- list break -->

- [ ] Requirements section complete

---

# DESIGN SPECIFICATIONS

*Only document properties/mappings that are NEW or CHANGED by this amendment.*

## New/Modified Properties in Figma

| Property Name | Type | Default | Variant Options | Description |
| --- | --- | --- | --- | --- |
|  |  |  |  |  |

## New/Modified Code Properties

| Property Name | Type | Required | Default | Description |
| --- | --- | --- | --- | --- |
|  |  |  |  |  |

## Updated Figma/Code Mapping

| Figma Property | Code Property | Mapping Logic | Notes |
| --- | --- | --- | --- |
|  |  |  |  |

## New Colour Contrast Pairings

[Only new pairings introduced by this amendment]

- [e.g., New tertiary variant text on background: 4.5:1]

## Design Assets

- Figma: [link to updated component]
- Original for reference: [link to original component]

<!-- list break -->

- [ ] Design specifications section complete

---

# CMS BEHAVIOUR CHANGES

*Only document changes to CMS behaviour - refer to original CRD for unchanged behaviour.*

#### Modified layout constraints

- [Only list what's changing]

#### New/Modified exposed properties

| Property | Options | Default | Notes |
| --- | --- | --- | --- |
|  |  |  |  |

#### Updated guardrails

- [Only new or changed validation rules]

<!-- list break -->

- [ ] CMS theming considerations section complete

---

# DEVELOPMENT GUIDANCE

## Technical Implementation Notes

- [Specific patterns for implementing this amendment]
- [How to integrate with existing component code]
- [Refactoring needed to support new functionality]

## Known Constraints

- [New limitations introduced]
- [Compatibility issues to watch for]

## Edge Cases

- [New edge cases introduced by amendment]
- [How amendment affects existing edge cases]

## Migration Notes

#### Backwards compatibility

- [Is this a breaking change?]
- [How existing uses are affected]

#### Upgrade path

- [Steps to adopt the amendment]
- [Code changes required for consumers]

#### Deprecation timeline

- [If removing features, when will they be removed]
- [Alternative patterns to use instead]

<!-- list break -->

- [ ] Development guidance section complete

---

# DOCUMENTATION GUIDANCE

## Design Notes

[Changes needed to existing component documentation]

## Implementation Insights

- [What was learned during implementation]
- [Unexpected complexities or simplifications]

## Usage Context

- [When to use the new functionality]
- [Migration guide for existing uses]
- [Updated examples showing new features]

## Migration & Changes

- **What changed:** [Detailed list of changes]
- **Why it changed:** [Rationale]
- **Breaking changes:** [Complete list with migration steps]
- **Deprecations:** [What's being phased out]

<!-- list break -->

- [ ] Documentation guidance section complete

---

# COMPLETION CHECKLIST

## Acceptance Criteria

#### Quality Standards

- [ ] Amendment built with semantic T3 design tokens (no hard-coded values)
- [ ] Tested across all brand themes in Figma
- [ ] All new colour pairings meet WCAG 2.1 AA contrast requirements
- [ ] Backwards compatibility verified (or breaking changes documented)
- [ ] Migration path tested with real examples
- [ ] Unit tests cover new/changed functionality
- [ ] Visual regression tests updated
- [ ] Performance impact assessed
- [ ] All requirements from Requirements section implemented
- [ ] *Add amendment-specific criteria*

#### AI Readiness

- [ ] Updated metadata reflects amendment changes
- [ ] Property naming remains consistent with original
- [ ] Documentation clearly distinguishes new from existing functionality
- [ ] *Add amendment-specific criteria*

<!-- list break -->

- [ ] Completion checklist section complete
`;
  }
  
  const config = await loadGitHubConfig();
  const path = config.docsPath + '/' + folder + '/' + filename;
  
  const createBtn = document.getElementById('createNewCrd');
  createBtn.disabled = true;
  createBtn.textContent = 'Creating...';
  try {
    const res = await apiPost('/api/create-crd', { path, content: template, message: 'Create ' + filename });
    const data = await res.json();
    console.log('📝 Create CRD API response:', data);
    
    if (data.success) {
      console.log('✅ File created successfully, now opening...');
      closeNewCrdModal();
      
      // Reload the file list to show the new file
      const section = document.querySelector('[data-folder="' + folder + '"]');
      const filesDiv = section.querySelector('.folder-files');
      await loadFilesIntoSection(folder, filesDiv);
      console.log('📋 File list reloaded');
      
      // Open the newly created file
      try {
        console.log('📂 Attempting to open file:', path);
        await openFile(path, filename.replace('.md', ''));
        console.log('✅ File opened successfully');
      } catch (openError) {
        console.error('❌ Failed to open newly created file:', openError);
        alert('File created but failed to open: ' + openError.message);
      }
    } else {
      console.error('❌ Create failed:', data.error);
      alert('Error: ' + data.error);
    }
  } catch (error) {
    alert('Error: ' + error.message);
  } finally {
    createBtn.disabled = false;
    createBtn.textContent = 'Create';
  }
}

document.getElementById('closeModal').addEventListener('click', closeNewCrdModal);
document.getElementById('cancelNewCrd').addEventListener('click', closeNewCrdModal);
document.getElementById('createNewCrd').addEventListener('click', createNewCrd);
document.getElementById('componentName').addEventListener('input', e => {
  document.getElementById('filenamePreview').textContent = 'Will create: ' + e.target.value.replace(/\s+/g, '_') + '_CRD.md';
});
document.getElementById('existingComponent').addEventListener('change', updateAmendmentPreview);
document.querySelectorAll('.segment-btn').forEach(btn => {
  btn.addEventListener('click', e => {
    if (!e.target?.dataset?.type) return;
    document.querySelectorAll('.segment-btn').forEach(b => b.classList.remove('active'));
    e.target.classList.add('active');
    const type = e.target.dataset.type;
    if (type === 'new') {
      document.getElementById('newComponentFields').style.display = 'block';
      document.getElementById('amendmentFields').style.display = 'none';
      setTimeout(() => document.getElementById('componentName').focus(), 50);
    } else {
      document.getElementById('newComponentFields').style.display = 'none';
      document.getElementById('amendmentFields').style.display = 'block';
      updateAmendmentPreview();
    }
  });
});
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') {
    const newCrdModal = document.getElementById('newCrdModal');
    const deleteModal = document.getElementById('deleteModal');
    const helpModal = document.getElementById('helpModal');
    if (newCrdModal.classList.contains('active')) closeNewCrdModal();
    if (deleteModal.classList.contains('active')) closeDeleteModal();
    if (helpModal.classList.contains('active')) closeHelpModal();
  }
});
document.getElementById('componentName').addEventListener('keypress', e => {
  if (e.key === 'Enter') createNewCrd();
});

// Delete modal event listeners
document.getElementById('closeDeleteModal').addEventListener('click', closeDeleteModal);
document.getElementById('cancelDelete').addEventListener('click', closeDeleteModal);
document.getElementById('confirmDeleteBtn').addEventListener('click', deleteFile);
document.getElementById('deleteConfirmInput').addEventListener('input', (e) => {
  const modal = document.getElementById('deleteModal');
  if (!modal?.dataset?.fileName) return;
  const expected = modal.dataset.fileName;
  const typed = e.target.value;
  document.getElementById('confirmDeleteBtn').disabled = typed !== expected;
});

// Help modal functions
async function openHelpModal() {
  const modal = document.getElementById('helpModal');
  const content = document.getElementById('helpContent');
  
  modal.classList.add('active');
  
  try {
    const res = await fetch('/api/github-file?path=docs/CRD_Workflow_Guide.md');
    const data = await res.json();
    
    // Simple markdown to HTML conversion
    const html = data.content
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      .replace(/```markdown\n([\s\S]*?)\n```/g, '<pre><code>$1</code></pre>')
      .replace(/`(.*?)`/g, '<code>$1</code>');
    
    content.innerHTML = '<p>' + html + '</p>';
  } catch (err) {
    content.innerHTML = '<p style="color: #d4145a;">Failed to load workflow guide. Please check GitHub repository.</p>';
  }
}

function closeHelpModal() {
  document.getElementById('helpModal').classList.remove('active');
}

document.getElementById('helpBtn').addEventListener('click', openHelpModal);
document.getElementById('closeHelpModal').addEventListener('click', closeHelpModal);

// Drag and drop handlers for file moving
let draggedItem = null;

function handleDragStart(e) {
  draggedItem = e.target;
  e.target.classList.add('dragging');
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', JSON.stringify({
    path: e.target.dataset.path,
    name: e.target.dataset.name,
    sourceFolder: e.target.dataset.folder
  }));
}

function handleDragEnd(e) {
  e.target.classList.remove('dragging');
  draggedItem = null;
}

function handleDragOver(e) {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  e.currentTarget.classList.add('drag-over');
}

function handleDragLeave(e) {
  e.currentTarget.classList.remove('drag-over');
}

async function handleDrop(e) {
  e.preventDefault();
  e.stopPropagation();
  
  const dropTarget = e.currentTarget;
  dropTarget.classList.remove('drag-over');
  
  try {
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const targetFolder = dropTarget.dataset.folder;
    
    // Don't move to same folder
    if (data.sourceFolder === targetFolder) {
      return;
    }
    
    // Call API to move file
    const response = await apiPost('/api/github/move', {
      path: data.path,
      name: data.name,
      sourceFolder: data.sourceFolder,
      targetFolder: targetFolder
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to move file');
    }
    
    // Refresh both folder sections
    const sourceFolderSection = document.querySelector(`[data-folder="${data.sourceFolder}"]`);
    const targetFolderSection = document.querySelector(`[data-folder="${targetFolder}"]`);
    
    if (sourceFolderSection) {
      const sourceFilesDiv = sourceFolderSection.querySelector('.folder-files');
      if (sourceFilesDiv && sourceFilesDiv.style.display === 'block') {
        loadFilesIntoSection(data.sourceFolder, sourceFilesDiv);
      }
    }
    
    if (targetFolderSection) {
      const targetFilesDiv = targetFolderSection.querySelector('.folder-files');
      if (targetFilesDiv && targetFilesDiv.style.display === 'block') {
        loadFilesIntoSection(targetFolder, targetFilesDiv);
      }
    }
    
    // If currently viewing the moved file, close the editor
    if (currentFile === data.path) {
      closeFile();
    }
    
  } catch (error) {
    console.error('Error moving file:', error);
    alert('Failed to move file: ' + error.message);
  }
}

/**
 * Get the current file path
 * @returns {string|null} The current file path or null if no file is open
 */
export function getCurrentFilePath() {
  return currentFile;
}

// Expose getCurrentFilePath globally for non-module scripts (like history.js)
window.getCurrentFilePath = getCurrentFilePath;